import type {
  CapitalBookSummaryV2,
} from "./read-models.js";
import type {
  CurrencyCode,
  DecimalValue,
  EvidenceReferenceV2,
  IsoDateTime,
  PortfolioBookKindV2,
  PortfolioV2SchemaVersion,
} from "./primitives.js";
import type {
  PortfolioPolicyIssueLevel,
  PortfolioPolicyState,
  PortfolioPolicyVersionV2,
} from "./policy.js";
import type {
  PortfolioPolicyAssetAllocationV2,
  PortfolioPolicyAssetClassSupportV2,
  PortfolioPolicyBenchmarkPolicyV2,
  PortfolioPolicyExitConstraintsV2,
  PortfolioPolicyHorizonPolicyV2,
  PortfolioPolicyPlannedCapitalFlowV2,
  PortfolioPolicySourceCostSelectionV2,
  PortfolioPolicyReviewGovernanceV2,
  PortfolioPolicyRevocationV2,
  PortfolioPolicySourceCostSnapshotV2,
  PortfolioPolicyWarningAcknowledgementInputV2,
} from "./policy-definition.js";

/**
 * Owner-entered values only. Lifecycle metadata (IDs, versions, states,
 * decisions and timestamps) is always created by the server.
 *
 * Nullable values are intentional: an incomplete policy is a valid saved
 * Draft, but it can never become an active policy.
 */
export interface PortfolioPolicyDraftInputV2 {
  definitionVersion: "portfolio-policy-definition.v2.2";
  baseCurrency: CurrencyCode | null;
  capitalBasis: PortfolioPolicyVersionV2["capitalBasis"] | null;
  /** Required only when capitalBasis is OWNER_DECLARED_CAPITAL. */
  declaredCapital: DecimalValue | null;
  /** Planning only: never mutates cash or creates a CashEventV2 automatically. */
  plannedCapitalFlows: PortfolioPolicyPlannedCapitalFlowV2[];
  maxOpenRiskPct: DecimalValue | null;
  maxTradeRiskPct: DecimalValue | null;
  /** Maximum exposure/concentration of one position, not a trade-risk limit. */
  maxPositionPct: DecimalValue | null;
  maxSectorExposurePct: DecimalValue | null;
  maxCorrelatedExposurePct: DecimalValue | null;
  /** Maximum portfolio peak-to-trough drawdown; separate from open risk. */
  maxPortfolioDrawdownPct: DecimalValue | null;
  minimumCashReservePct: DecimalValue | null;
  minimumRewardToRisk: DecimalValue | null;
  slippageAllowancePerUnit: DecimalValue | null;
  allowFractionalQuantity: boolean | null;
  horizonPolicy: PortfolioPolicyHorizonPolicyV2 | null;
  assetAllocationPolicy: PortfolioPolicyAssetAllocationV2 | null;
  exitConstraints: PortfolioPolicyExitConstraintsV2 | null;
  benchmarkPolicy: PortfolioPolicyBenchmarkPolicyV2 | null;
  sourceCostPolicy: PortfolioPolicySourceCostSelectionV2 | null;
}

export const PORTFOLIO_POLICY_DRAFT_FIELDS_V2 = [
  "definitionVersion",
  "baseCurrency",
  "capitalBasis",
  "declaredCapital",
  "plannedCapitalFlows",
  "maxOpenRiskPct",
  "maxTradeRiskPct",
  "maxPositionPct",
  "maxSectorExposurePct",
  "maxCorrelatedExposurePct",
  "maxPortfolioDrawdownPct",
  "minimumCashReservePct",
  "minimumRewardToRisk",
  "slippageAllowancePerUnit",
  "allowFractionalQuantity",
  "horizonPolicy",
  "assetAllocationPolicy",
  "exitConstraints",
  "benchmarkPolicy",
  "sourceCostPolicy",
] as const;

export type PortfolioPolicyDraftFieldV2 = typeof PORTFOLIO_POLICY_DRAFT_FIELDS_V2[number];

export const PORTFOLIO_POLICY_LIFECYCLE_ISSUE_FIELDS_V2 = [
  "schemaVersion",
  "bookId",
  "bookKind",
  "bookBaseCurrency",
  "reviewEvidenceRefs",
  "warningAcknowledgements",
  ...PORTFOLIO_POLICY_DRAFT_FIELDS_V2,
] as const;

export type PortfolioPolicyLifecycleIssueFieldV2 = typeof PORTFOLIO_POLICY_LIFECYCLE_ISSUE_FIELDS_V2[number];

export const PORTFOLIO_POLICY_LIFECYCLE_ISSUE_CODES_V2 = [
  "POLICY_SCHEMA_VERSION_INVALID",
  "POLICY_BOOK_CONTEXT_INVALID",
  "POLICY_DRAFT_FIELD_REQUIRED",
  "POLICY_DRAFT_SHAPE_INVALID",
  "POLICY_BOOK_CURRENCY_MISMATCH",
  "POLICY_CAPITAL_BASIS_INVALID",
  "INVALID_CURRENCY",
  "INVALID_DECIMAL",
  "PERCENTAGE_OUT_OF_RANGE",
  "NEGATIVE_POLICY_VALUE",
  "DECLARED_CAPITAL_REQUIRED",
  "POLICY_CURRENT_EQUITY_REQUIRED",
  "TRADE_RISK_EXCEEDS_OPEN_RISK",
  "HORIZON_ALLOCATION_MUST_EQUAL_ONE",
  "HORIZON_POLICY_INVALID",
  "ASSET_ALLOCATION_INVALID",
  "ASSET_TARGET_ALLOCATION_MUST_EQUAL_ONE",
  "CASH_ALLOCATION_BELOW_RESERVE",
  "POSITION_EXCEEDS_CONCENTRATION_LIMIT",
  "PLANNED_CAPITAL_FLOW_INVALID",
  "EXIT_CONSTRAINTS_INVALID",
  "BENCHMARK_REFERENCE_INVALID",
  "SOURCE_COST_REFERENCE_INVALID",
  "REVIEW_EVIDENCE_INVALID",
] as const;

export type PortfolioPolicyLifecycleIssueCodeV2 = typeof PORTFOLIO_POLICY_LIFECYCLE_ISSUE_CODES_V2[number];

export { PORTFOLIO_POLICY_RULE_IDS_V2 } from "./policy-rule-ids.js";
export type { PortfolioPolicyRuleIdV2 } from "./policy-rule-ids.js";
import type { PortfolioPolicyRuleIdV2 } from "./policy-rule-ids.js";

export const PORTFOLIO_POLICY_LIFECYCLE_ERROR_CODES_V2 = [
  "POLICY_BOOK_NOT_FOUND",
  "POLICY_BOOK_NOT_ACTIVE",
  "POLICY_BOOK_VERSION_CONFLICT",
  "POLICY_OWNER_CONFIRMATION_REQUIRED",
  "POLICY_DRAFT_NOT_FOUND",
  "POLICY_DRAFT_VERSION_CONFLICT",
  "POLICY_DRAFT_FINGERPRINT_CONFLICT",
  "POLICY_DRAFT_ALREADY_ACTIVATED",
  "POLICY_DRAFT_NOT_READY",
  "POLICY_BOOK_CONTEXT_INVALID",
  "POLICY_DRAFT_CONTEXT_INVALID",
  "POLICY_REQUEST_INVALID",
  "POLICY_IDENTIFIER_INVALID",
  "POLICY_DRAFT_VALUE_INVALID",
  "POLICY_OWNER_NOTE_INVALID",
  "POLICY_ACTIVE_NOT_FOUND",
  "POLICY_REVOKE_REASON_INVALID",
  "POLICY_WARNING_ACKNOWLEDGEMENT_REQUIRED",
  "POLICY_REVIEW_EVIDENCE_INVALID",
  "POLICY_DEFINITION_ENFORCEMENT_CONTEXT_REQUIRED",
  "POLICY_VERSION_INVALID",
  "POLICY_FINGERPRINT_INVALID",
  "POLICY_IDEMPOTENCY_CONFLICT",
  "POLICY_PERSISTED_STATE_CORRUPT",
  "POLICY_SCHEMA_VERSION_INVALID",
  "POLICY_OPERATION_ABANDONED",
  "POLICY_OPERATION_RESOLUTION_CONFLICT",
] as const;

export type PortfolioPolicyLifecycleErrorCodeV2 = typeof PORTFOLIO_POLICY_LIFECYCLE_ERROR_CODES_V2[number];

export interface PortfolioPolicyLifecycleFailureV2 {
  ok: false;
  code: PortfolioPolicyLifecycleErrorCodeV2;
  detail?: Record<string, unknown>;
}

export interface PortfolioPolicyLifecycleIssueV2 {
  code: PortfolioPolicyLifecycleIssueCodeV2;
  copyKey: PortfolioPolicyLifecycleIssueCodeV2;
  level: PortfolioPolicyIssueLevel;
  ruleId: PortfolioPolicyRuleIdV2;
  ruleVersion: "portfolio-policy-rule.v2.2";
  field: PortfolioPolicyLifecycleIssueFieldV2;
  inputValue: DecimalValue | string | boolean | null;
  allowedValue: DecimalValue | string | boolean | null;
}

export const PORTFOLIO_POLICY_DRAFT_VALIDATION_STATES_V2 = [
  "INCOMPLETE",
  "INVALID",
  "READY_FOR_ACTIVATION",
] as const;

export type PortfolioPolicyDraftValidationStateV2 = typeof PORTFOLIO_POLICY_DRAFT_VALIDATION_STATES_V2[number];

export const PORTFOLIO_POLICY_DOLLAR_PREVIEW_STATES_V2 = ["UNAVAILABLE", "PARTIAL", "COMPLETE"] as const;
export type PortfolioPolicyDollarPreviewStateV2 = typeof PORTFOLIO_POLICY_DOLLAR_PREVIEW_STATES_V2[number];

/** Exact-money preview. Null means that the corresponding input was absent or invalid. */
export interface PortfolioPolicyDollarPreviewV2 {
  status: PortfolioPolicyDollarPreviewStateV2;
  currency: CurrencyCode | null;
  capitalBaseAmount: DecimalValue | null;
  maxOpenRiskAmount: DecimalValue | null;
  maxTradeRiskAmount: DecimalValue | null;
  maxPositionAmount: DecimalValue | null;
  maxSectorExposureAmount: DecimalValue | null;
  maxCorrelatedExposureAmount: DecimalValue | null;
  maxPortfolioDrawdownAmount: DecimalValue | null;
  minimumCashReserveAmount: DecimalValue | null;
  capitalAfterMinimumCashReserve: DecimalValue | null;
}

/** Server-resolved book facts; the client does not choose or override these. */
export interface PortfolioPolicyValidationBookContextV2 {
  bookId: string;
  kind: PortfolioBookKindV2;
  baseCurrency: CurrencyCode;
  currentEquity: {
    value: DecimalValue;
    asOf: IsoDateTime;
    sourceId: string;
  } | null;
  /** Broker sources already scoped and authorized for this book by the server. */
  brokerSources: Array<{
    brokerSourceId: string;
    feeScheduleRef: string | null;
  }>;
  assetClassSupport: PortfolioPolicyAssetClassSupportV2[];
}

export interface ValidatePortfolioPolicyDraftRequestV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  book: PortfolioPolicyValidationBookContextV2;
  draft: PortfolioPolicyDraftInputV2;
}

export interface PortfolioPolicyDraftValidationV2 {
  status: PortfolioPolicyDraftValidationStateV2;
  normalizedDraft: PortfolioPolicyDraftInputV2;
  issues: PortfolioPolicyLifecycleIssueV2[];
  dollarPreview: PortfolioPolicyDollarPreviewV2;
  evaluatedAt: IsoDateTime;
  formulaVersion: "portfolio-policy-lifecycle.v2.2";
  inputFingerprint: string;
}

export interface ValidatePortfolioPolicyDraftResponseV2 {
  ok: true;
  validation: PortfolioPolicyDraftValidationV2;
}

/** Saving accepts incomplete input; the server validates and versions it again. */
export interface SavePortfolioPolicyDraftInputV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  idempotencyKey: string;
  bookId: string;
  expectedBookVersion: number;
  draft: PortfolioPolicyDraftInputV2;
}

/**
 * All references are returned by the server when a Draft is saved. The owner
 * confirmation is explicit and activation still requires server-side Step-up
 * for an Owned Private book.
 */
export interface ActivatePortfolioPolicyDraftInputV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  idempotencyKey: string;
  bookId: string;
  policyVersionId: string;
  expectedBookVersion: number;
  expectedPolicyVersion: number;
  inputFingerprint: string;
  ownerConfirmation: boolean;
  ownerDecisionNote: string | null;
  reviewEvidenceRefs: EvidenceReferenceV2[];
  warningAcknowledgements: PortfolioPolicyWarningAcknowledgementInputV2[];
}

export interface RevokePortfolioPolicyInputV2 {
  schemaVersion: PortfolioV2SchemaVersion;
  idempotencyKey: string;
  bookId: string;
  activePolicyVersionId: string;
  expectedBookVersion: number;
  expectedPolicyVersion: number;
  ownerConfirmation: boolean;
  reason: string;
}

export type {
  ActivatePortfolioPolicyDraftResponseV2,
  PortfolioPolicyLifecycleReadModelV2,
  PortfolioPolicyLifecycleReadResponseV2,
  PortfolioPolicyValidationEvidenceStateV2,
  PortfolioPolicyVersionReadModelV2,
  RevokePortfolioPolicyResponseV2,
  SavePortfolioPolicyDraftResponseV2,
} from "./policy-lifecycle-read-models.js";
export { PORTFOLIO_POLICY_VALIDATION_EVIDENCE_STATES_V2 } from "./policy-lifecycle-read-models.js";

export type PortfolioPolicyLifecycleResponseV2<T> = T | PortfolioPolicyLifecycleFailureV2;
